import { Box, Text } from 'grommet'
import 'quill-emoji/dist/quill-emoji.css'
import { CSSProperties, ReactNode, forwardRef, type MouseEvent } from 'react'
import ReactQuill, { Quill, ReactQuillProps } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
// @ts-ignore
import * as Emoji from 'quill-emoji'
import { EditorContainer } from './style'

// Register emoji module
Quill.register('modules/emoji', Emoji)

// Undo and redo functions
function undoChange(this: any) {
  this.quill.history.undo()
}

function redoChange(this: any) {
  this.quill.history.redo()
}

// Custom toolbar icons
const icons = Quill.import('ui/icons')
icons.undo =
  '<svg viewBox="0 0 18 18"><polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon><path class="ql-stroke" d="M8.09,13.91A6,6,0,1,1,13.91,8.09"></path></svg>'
icons.redo =
  '<svg viewBox="0 0 18 18"><polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon><path class="ql-stroke" d="M9.91,13.91A6,6,0,1,0,4.09,8.09"></path></svg>'

// Toolbar configuration
const modules = {
  'emoji-shortname': true,
  'emoji-textarea': false,
  'emoji-toolbar': true,
  history: {
    delay: 1000,
    maxStack: 100,
    userOnly: true
  },
  toolbar: {
    container: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }], // custom dropdown
      [{ color: [] }, { background: [] }],
      ['link', 'image', 'emoji'],
      ['clean'],
      ['undo', 'redo']
    ],
    handlers: {
      redo: redoChange,
      undo: undoChange
    },
    icons: {
      undo: icons.undo
    }
  }
}

type EditorInputProps = ReactQuillProps & {
  label?: string
  round?: string
  heading?: string
  className?: string
  headerStyle?: CSSProperties
  boxClass?: string
  error?: string
  footer?: ReactNode
  borderRadius?: string
  module?: any
  button?: ReactNode
  minHeight?: string
}

const RichTextEditor = forwardRef<ReactQuill, EditorInputProps>(
  (
    {
      placeholder,
      label,
      value,
      heading,
      onChange,
      round = heading && '0px',
      className,
      module,
      headerStyle,
      borderRadius,
      error,
      boxClass,
      footer,
      button,
      minHeight,
      ...rest
    },
    ref
  ) => {
    const handleToolbarMouseDown = (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement
      if (target.closest('.ql-toolbar, .ql-picker-options, .ql-emoji-picker')) {
        event.stopPropagation()
      }
    }

    return (
      <>
        <EditorContainer
          $borderRadius={borderRadius}
          $minHeight={minHeight}
          className={`${heading || footer ? `rounded-[15px] flex flex-col border border-solid overflow-hidden border-[#999999] ${boxClass}` : ''} `}
        >
          {heading && (
            <Box direction="row" justify="between" pad={'7px'} style={headerStyle}>
              <Text style={{ fontSize: '18px', fontWeight: 500 }}>{heading}</Text>
              {button}
            </Box>
          )}
          {!heading && label && (
            <Text margin={{ bottom: '2px' }} style={{ fontSize: '14px', fontWeight: 500 }}>
              {label}
            </Text>
          )}
          <Box className="flex-1" onMouseDown={handleToolbarMouseDown}>
            <ReactQuill
              className={`${heading || footer ? '!border-none' : ''} ${className}`}
              modules={module || modules}
              onChange={onChange}
              placeholder={placeholder || 'Enter text here'}
              ref={ref}
              value={value}
              {...rest}
            />
            {error && (
              <Box pad={{ left: 'small' }}>
                <Text color="status-error" size="small">
                  {error}
                </Text>
              </Box>
            )}
          </Box>

          {footer && <Box>{footer}</Box>}
        </EditorContainer>
      </>
    )
  }
)

RichTextEditor.displayName = 'RichTextEditor'

export default RichTextEditor
